import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';
import { NestedCSSProperties } from 'typestyle/lib/types';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { imgUrl } from '~src/commonInfra/utils/ImgUrl';
import { applyColor } from '~src/style/ColorService';

import { cssClass, Style, textEllipsis } from '../../../style';

export const THUMBNAIL_HEIGHT = 184;
export const THUMBNAIL_BORDER_HEIGHT = 2; // 1px top and bottom 
const CARD_INFO_HEIGHT = 41;

const NUM_LINES_TEXT_TO_SHOW = 2;
const LINE_HEIGHT_IN_EM = 1.5; // from .tb-app line-height
const PRIMARY_FONT_SIZE = 14;
const DESCRIPTION_FONT_SIZE = TabStyles.Typography.Sizing.Default;
export const CARD_DESCRIPTION_HEIGHT = NUM_LINES_TEXT_TO_SHOW * DESCRIPTION_FONT_SIZE * LINE_HEIGHT_IN_EM;

export const TOTAL_CARD_HEIGHT = THUMBNAIL_HEIGHT + THUMBNAIL_BORDER_HEIGHT + CARD_INFO_HEIGHT + CARD_DESCRIPTION_HEIGHT;
export const TOTAL_CARD_WIDTH = 300;

export const CARD_POPOVER_WIDTH = 400;

const BOX_SHADOW = '0px 1px 2px ' + applyColor(DesignTokens.color.elevation.surface.default.default, 'rgba(0,0,0,.15)');
const BORDER = '1px solid ' + applyColor(DesignTokens.color.elevation.border.default.subtle, '#d8d8d8');
const CARD_BUTTON_DIMENSION = 22;

// "Application Blue" with alpha for selection/hover
const APPLICATION_BLUE = applyColor(DesignTokens.color.elevation.surface.default.dark_alt, 'rgba(34, 38, 54)');
const LIGHT_HIGHLIGHT_COLOR = applyColor(DesignTokens.color.elevation.surface.default.dark_alt, 'rgba(34, 38, 54)');

const cssPrefix = 'CardView';

export namespace CardViewStyles {

  const BaseCardStyle: Style = {
    fontWeight: 'normal',
    position: 'relative',
    // Many thumbnail elements have a hardcoded positive z-index; explicitly declaring 0 here
    // forces a new stacking context so the thumbnail doesn't float above other elements such as dropdowns
    zIndex: 0,
    fontSize: TabStyles.Typography.Sizing.Default,
  };

  export const ElideText = cssClass(cssPrefix, 'elide-text', textEllipsis);

  export const CardWrapperFocusClass = cssClass(cssPrefix, 'card-wrapper-focus', {
    $nest: {
      '&:focus-visible': CommonStyles.Focus.OutlineIndicator,
    }
  });

  export const ThumbnailFocus = cssClass(cssPrefix, 'thumbnail-focus', {
    $nest: {
      '&:focus-visible': CommonStyles.Focus.OutlineIndicator
    }
  });

  export const SelectCheckBoxTop = 10;
  export const SelectCheckBoxLeft = 7;
  export const SelectCheckbox = cssClass(cssPrefix, 'select-checkbox', {
    position: 'absolute',
    top: SelectCheckBoxTop,
    left: SelectCheckBoxLeft,
    zIndex: 1,
  });

  const ThumbnailBaseStyle: Style = {
    display: 'block',
    height: THUMBNAIL_HEIGHT,
    backgroundRepeat: 'no-repeat',
    borderRadius: 1,
    border: BORDER,
    boxShadow: BOX_SHADOW,
    position: 'relative',
    zIndex: 3,
  };

  export const ThumbnailPreloaded = cssClass(cssPrefix, 'thumbnail-preloaded', ThumbnailBaseStyle);

  export const Thumbnail = cssClass(cssPrefix, 'thumbnail', ThumbnailBaseStyle, {
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, '#fafafa'),
    backgroundPosition: 'center',
  });

  export const ThumbnailCenteredText = cssClass(cssPrefix, 'thumbnail-centered-text', {
    color: applyColor(DesignTokens.color.font.default.default, CommonStyles.Colors.Grey51),
    fontSize: '14px',
    left: '50%',
    position: 'absolute',
    top: '50%',
    transform: 'translateX(-50%) translateY(-50%)',
    '-ms-transform': 'translateX(-50%) translateY(-50%)',
  });

  export const DarkModeThumbnailOverlay = cssClass(cssPrefix, 'dark-mode-thumbnail-overlay', {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'black',
    opacity: 0.75,
  });

  export const DarkModeThumbnailWatermark = cssClass(cssPrefix, 'dark-mode-thumbnail-watermark', {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: DesignTokens.color.elevation.surface.default.hint,
    color: DesignTokens.color.font.default.default,
    borderRadius: 6,
    padding: 10,
    maxWidth: '90%',
    overflow: 'hidden',
    $nest: {
      'svg': {
        width: 30
      }
    }
  });

  export const DarkModeThumbnailWatermarkText = cssClass(cssPrefix, 'dark-mode-thumbnail-watermark-text', {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  });

  export const Card = cssClass(cssPrefix, 'card-inner', BaseCardStyle, {
    $nest: {
      '&>span': {
        display: 'block'
      }
    }
  });

  const RECOMMENDATION_RIBBON_TEXT_OFFSET = '13px';
  const RECOMMENDATION_RIBBON_SHAPE_OFFSET = '0';

  export const RecommendationExplanation = cssClass(cssPrefix, 'recommendation-explanation', {});
  export const SelectedRecommendationCard = cssClass(cssPrefix, 'selected-recommendation-card', {});

  const recommendationRibbonVisibleSelector = (child: 'span' | 'img') =>
    `&:hover .${RecommendationExplanation} > ${child}, ` +
    `&:has(:focus-visible) .${RecommendationExplanation} > ${child}, ` +
    `&.${SelectedRecommendationCard} .${RecommendationExplanation} > ${child}`;

  export const RecommendationCard = cssClass(cssPrefix, 'recommendation-card', {
    overflow: 'hidden',
    $nest: {
      [recommendationRibbonVisibleSelector('span')]: {
        right: RECOMMENDATION_RIBBON_TEXT_OFFSET
      },
      [recommendationRibbonVisibleSelector('img')]: {
        right: RECOMMENDATION_RIBBON_SHAPE_OFFSET
      }
    }
  });

  const RecommendationCardShapeBase: Style = {
    position: 'absolute',
    zIndex: 4,
    pointerEvents: 'none',
    right: '-57px',

    transition: 'all .5s ease'
  };

  export const RecommendationCardShape = cssClass(cssPrefix, 'recommendation-card-shape', {
    ...RecommendationCardShapeBase
  });

  export const RecommendationCardDCRShapeEn = cssClass(cssPrefix, 'recommendation-card-shape', {
    ...RecommendationCardShapeBase,
    right: '-80px'
  });

  export const RecommendationCardForYouShapeLong = cssClass(cssPrefix, 'recommendation-card-shape', {
    ...RecommendationCardShapeBase,
    right: '-117px'
  });

  export const RecommendationCardMidShape = cssClass(cssPrefix, 'recommendation-card-shape', {
    ...RecommendationCardShapeBase,
    right: '-127px'
  });

  export const RecommendationCardLongShape = cssClass(cssPrefix, 'recommendation-card-shape', {
    ...RecommendationCardShapeBase,
    right: '-157px'
  });

  const RecommendationCardTextBase: Style = {
    fontWeight: 500,
    position: 'absolute',
    zIndex: 5,
    right: '-115px',
    top: '6px',
    pointerEvents: 'none',
    fontSize: TabStyles.Typography.Sizing.Small,
    color: applyColor(DesignTokens.color.font.default.default, TabStyles.Typography.Color.InvertedPrimary),

    transition: 'all .5s ease'
  };

  export const RecommendationCardText = cssClass(cssPrefix, 'recommendation-card-text', {
    ...RecommendationCardTextBase
  });

  export const RecommendationCardLongText = cssClass(cssPrefix, 'recommendation-card-text', {
    ...RecommendationCardTextBase,
    right: '-138px',
  });

  // Workbook thumbnails are decorated to look like a stack of cards.
  const ThumbnailWorkbookDecorationBase: Style = {
    display: 'block',
    height: THUMBNAIL_HEIGHT,
    boxShadow: BOX_SHADOW,
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.default, TabStyles.Colors.White),
    position: 'absolute',
  };

  const WorkbookDecorationSpacing = 9;

  export const ThumbnailWorkbookDecoration = cssClass(cssPrefix, 'thumbnail-workbook-decoration', {
    ...ThumbnailWorkbookDecorationBase,
    width: TOTAL_CARD_WIDTH - (WorkbookDecorationSpacing * 2),
    top: WorkbookDecorationSpacing,
    left: WorkbookDecorationSpacing,
    zIndex: 1,
    $nest: {
      '&::before': {
        ...ThumbnailWorkbookDecorationBase,
        content: `''`,
        width: TOTAL_CARD_WIDTH - WorkbookDecorationSpacing,
        top: -4,
        left: -4
      }
    }
  });

  export const SelectOverlay = cssClass(cssPrefix, 'card-select-overlay', {
    display: 'none'
  });

  const cardHighlightBase: NestedCSSProperties = {
    display: 'block',
    position: 'absolute',
    height: THUMBNAIL_HEIGHT,
    width: '100%',
    top: 0,
    left: 0,
    zIndex: 4,
    backgroundColor: APPLICATION_BLUE,
    opacity: 0.5,
    borderRadius: 1,
    pointerEvents: 'none'
  };

  export const CardSelectionHighlight = cssClass(cssPrefix, 'card-selection-highlight', {
    $nest: {
      [`.${SelectOverlay}`]: {
        ...cardHighlightBase,
        backgroundColor: APPLICATION_BLUE,
        opacity: 0.5,
      }
    }
  });

  export const CardHoverHighlight = cssClass(cssPrefix, 'card-hover-highlight', {
    $nest: {
      [`.${SelectOverlay}`]: {
        ...cardHighlightBase,
        backgroundColor: APPLICATION_BLUE,
        opacity: 0.25
      }
    }
  });

  export const SelectedClassName = 'selected';
  export const HoveredClassName = 'hovered';
  const customThumbnailBase: Style = {
    height: THUMBNAIL_HEIGHT,
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.default, '#FAFAFA'),
    boxShadow: BOX_SHADOW,
    borderRadius: 1,
    border: BORDER,
    position: 'relative',
    zIndex: 3,

    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    alignItems: 'center',

    boxSizing: 'border-box',
    paddingTop: TabStyles.Spacing.LargeSpace,
    paddingBottom: TabStyles.Spacing.LargeSpace,
    justifyContent: 'center',

    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Typography.Color.PrimaryLight),

    $nest: {
      [`&.${SelectedClassName}::after`]: {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: APPLICATION_BLUE,
        opacity: 0.5,
        borderRadius: 1,
        pointerEvents: 'none',
        zIndex: 0,
      },
      [`&.${HoveredClassName}::after`]: {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: APPLICATION_BLUE,
        opacity: 0.25,
        borderRadius: 1,
        pointerEvents: 'none',
        zIndex: 0,
      },
      '& > div': {
        position: 'relative' as const,
        zIndex: 1,
      },
      '& > a': {
        zIndex: 1,
      },
      [`&.${HoveredClassName}`]: {
        $nest: {
          'a': {
            background: 'none'
          }
        }
      },
      'a': {
        background: 'linear-gradient(to bottom, transparent 70%, ' + applyColor(DesignTokens.color.elevation.surface.default.subtle, 'rgba(250,250,250,1)') + ' 100%)'
      }
    }
  };

  export const CustomThumbnail = cssClass(cssPrefix, 'custom-thumbnail', customThumbnailBase, {
    cursor: 'pointer'
  });

  export const CustomThumbnailNoLinkPointer = cssClass(cssPrefix, 'custom-thumbnail-no-link-pointer', customThumbnailBase, {
    $nest: {
      '& a': {
        cursor: 'default'
      }
    }
  });

  const customThumbnailImgBase: Style = {
    width: 60,
    height: 60,
    marginBottom: TabStyles.Spacing.SmallSpace,
    marginLeft: 'auto',
    marginRight: 'auto'
  };

  export const CustomThumbnailImg = cssClass(cssPrefix, 'custom-thumbnail-img', {
    ...customThumbnailImgBase
  });

  export const DatabaseCustomThumbnailImg = cssClass(cssPrefix, 'database-custom-thumbnail-img', {
    ...customThumbnailImgBase,
    marginBottom: TabStyles.Spacing.ExtraLargeSpace
  });

  export const DatabaseCustomSecondTitle = cssClass(cssPrefix, 'database-custom-thumbnail-second-title', {
    fontSize: DESCRIPTION_FONT_SIZE,
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Typography.Color.PrimaryLight),
  });

  export const DatasourceCustomThumbnailImg = cssClass(cssPrefix, 'datasource-custom-thumbnail-img', {
    ...customThumbnailImgBase,
    marginBottom: TabStyles.Spacing.ExtraLargeSpace
  });

  export const CustomThumbnailTopContainer = cssClass(cssPrefix, 'custom-thumbnail-top-container', {
    display: 'block',
    margin: 'auto',
    textAlign: 'center',
    fontSize: PRIMARY_FONT_SIZE,
    color: applyColor(DesignTokens.color.font.default.default, TabStyles.Typography.Color.Primary),
    width: '100%',
    overflow: 'hidden'
  });

  export const DataconnectionThumbnailDatasourceLink = cssClass(cssPrefix, 'dataconection-thumbnail-datasource-link', {
    marginBottom: TabStyles.Spacing.SmallSpace,
    marginLeft: TabStyles.Spacing.MediumSpace,
    marginRight: TabStyles.Spacing.MediumSpace
  });

  export const DataconnectionThumbnailConnectionType = cssClass(cssPrefix, 'dataconnection-thumbnail-connection-type', {
    marginBottom: TabStyles.Spacing.SmallSpace,
    color: applyColor(DesignTokens.color.font.default.default, TabStyles.Typography.Color.Primary),
    fontSize: PRIMARY_FONT_SIZE
  });

  export const DataconnectionThumbnailUsername = cssClass(cssPrefix, 'dataconnection-thumbnail-username', {
    fontStyle: 'italic',
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Typography.Color.PrimaryLight)
  });

  export const ActionRow = cssClass(cssPrefix, 'action-row', {
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: TabStyles.Spacing.MediumSpace,
    paddingRight: TabStyles.Spacing.SmallSpace,
    $nest: {
      '&>*': {
        height: CARD_BUTTON_DIMENSION,
        paddingRight: TabStyles.Spacing.DefaultSpace
      }
    }
  });

  const ActionRowNameStyle = {
    flex: 1,
    fontWeight: 500,
    fontSize: PRIMARY_FONT_SIZE,
    paddingRight: TabStyles.Spacing.MediumSpace,
    paddingTop: TabStyles.Spacing.SmallSpace
  };

  export const ActionRowName = cssClass(cssPrefix, 'action-row-name', textEllipsis, ActionRowNameStyle, { margin: 0 });

  export const ActionRowNameMissing = cssClass(cssPrefix, 'action-row-name-missing', textEllipsis, {
    ...ActionRowNameStyle,
    fontStyle: 'italic',
    color: applyColor(DesignTokens.color.font.default.subtle, CommonStyles.Colors.Grey178)
  });

  export const CardWrapper = cssClass(cssPrefix, 'card-wrapper', {
    $nest: {
      ['&.compact-title .' + ActionRow]: {
        paddingLeft: 0,
        paddingRight: 0,
        $nest: {
          '&>*': {
            paddingRight: 0
          },
          ['& .tb-favorite-button']: {
            display: 'none'
          }
        }
      }
    }
  });

  export const getActionRowInfo = () => cssClass(cssPrefix, 'action-row-info', {
    backgroundImage: imgUrl('data_prep_info.svg'),
    height: '18px',
    width: '18px',
    display: 'inline-block',
    backgroundRepeat: 'no-repeat',
    padding: '3px',
    backgroundPosition: '3px 3px',
    backgroundSize: '18px 18px',
    $nest: {
      '&:hover': {
        cursor: 'pointer'
      }
    }
  });

  export const CardDescription = cssClass(cssPrefix, 'card-description', {
    fontSize: DESCRIPTION_FONT_SIZE,
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Typography.Color.PrimaryLight),
    overflow: 'hidden',
    position: 'relative',
    maxHeight: CARD_DESCRIPTION_HEIGHT,
    paddingRight: TabStyles.Spacing.MediumSpace,
    paddingLeft: TabStyles.Spacing.MediumSpace,
    textOverflow: 'ellipsis',
  });

  export const CardLastAccessedAt = cssClass(cssPrefix, 'card-last-accessed-at', {
    fontSize: DESCRIPTION_FONT_SIZE,
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Typography.Color.PrimaryLight),
    overflow: 'hidden',
    position: 'relative',
    maxHeight: CARD_DESCRIPTION_HEIGHT,
    paddingRight: 27,
    paddingLeft: TabStyles.Spacing.MediumSpace,
  });

  export const CardNoLeftPadding = cssClass(cssPrefix, 'card-no-left-padding', {
    paddingLeft: 0
  });

  export const CardTextAndLinkMaxWidth = TOTAL_CARD_WIDTH - 18; // (minus the padding)
  export const CardTextAndLink = cssClass(cssPrefix, 'card-text-and-link', {
    textOverflow: 'ellipsis',
    maxWidth: CardTextAndLinkMaxWidth,
    paddingRight: 9,
    paddingLeft: 9,
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  });

  export const AnchorUnderlay = cssClass(cssPrefix, 'anchor-underlay', {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  });

  export const VizRecViewsIcon = cssClass(cssPrefix, 'vizrec-views-icon', {
    display: 'inline-block',
    verticalAlign: 'text-bottom',
    width: '18px',
    height: '18px',
    marginTop: '2px'
  });

  const RecCardCommon = {
    color: applyColor(DesignTokens.color.font.default.default, CommonStyles.Colors.NormalTextColor),
    fontSize: TabStyles.Typography.Sizing.Small,
    display: 'inline-block',
    width: 'auto',
    paddingTop: TabStyles.Spacing.SmallSpace,
    fontVariantNumeric: 'tabular-nums'
  };

  export const VizRecViewsCount = cssClass(cssPrefix, 'vizrec-views-count', {
    ...RecCardCommon,
    marginLeft: TabStyles.Spacing.DefaultSpace,
    marginRight: '10px'
  });

  export const VizRecFavoritesCount = cssClass(cssPrefix, 'vizrec-favorites-count', {
    ...RecCardCommon,
    marginLeft: '-2px',
    marginRight: TabStyles.Spacing.MediumSpace,
  });

  export const SharedWithMeContent = cssClass(cssPrefix, 'shared-with-me-content', {
    display: 'flex',
    paddingLeft: TabStyles.Spacing.MediumSpace,
    paddingRight: TabStyles.Spacing.MediumSpace,
  });

  export const SharedWithMeProfilePicture = cssClass(cssPrefix, 'shared-with-me-profile-picture', {
    width: '34px',
    height: '34px',
    paddingRight: '8px',
  });

  export const SharedWithMeSharerAndText = cssClass(cssPrefix, 'shared-with-me-sharer-and-text', {
    flexDirection: 'column',
  });

  export const SharedWithMeSingleLine = cssClass(cssPrefix, 'shared-with-me-single-line', {
    display: 'flex',
    height: '18px',
    width: '230px',
  });

  export const SharedWithMeSharerNameSingleLine = cssClass(cssPrefix, 'shared-with-me-sharer-name-single-line', {
    flexShrink: 1,
    marginRight: TabStyles.Spacing.SmallSpace,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  });

  export const CollectionCardInfoLayout = cssClass(cssPrefix, 'card-info-layout', {
    position: 'absolute',
    zIndex: 4,
    top: '1px',
    left: '1px',
    width: TOTAL_CARD_WIDTH - 2,
    height: THUMBNAIL_HEIGHT,
    pointerEvents: 'none',
    display: 'flex',
    flexDirection: 'column'
  });

  export const CollectionCardTypeLabelArea = cssClass(cssPrefix, 'card-type-label-area', {
    paddingLeft: TabStyles.Spacing.MediumSpace,
    paddingRight: TabStyles.Spacing.MediumSpace,
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  });

  export const CollectionCardTypeLabel = cssClass(cssPrefix, 'card-type-label', textEllipsis, {
    fontWeight: 500,
    fontSize: TabStyles.Typography.Sizing.Default,
    color: applyColor(DesignTokens.color.font.default.secondary, CommonStyles.Colors.Grey102),
    letterSpacing: '0.5px',
    paddingTop: TabStyles.Spacing.MediumSpace,
    paddingBottom: TabStyles.Spacing.MediumSpace,
  });

  export const CollectionCardTitleArea = cssClass(cssPrefix, 'card-title-area', {
    paddingLeft: TabStyles.Spacing.MediumSpace,
    paddingRight: TabStyles.Spacing.MediumSpace,
    paddingTop: 24,
  });

  export const CollectionCardTitleText = cssClass(cssPrefix, 'card-title-text', {
    fontSize: '34px',
    color: applyColor(DesignTokens.color.font.default.default, CommonStyles.Colors.Grey51),
    lineHeight: '44px',
    maxHeight: '88px',
    overflow: 'hidden'
  });

  export const PrivateCollectionArea = cssClass(cssPrefix, 'card-private-collection-area', textEllipsis, {
    padding: TabStyles.Spacing.MediumSpace
  });

  export const PrivateCollectionText = cssClass(cssPrefix, 'card-private-collection-label', {
    paddingLeft: TabStyles.Spacing.DefaultSpace,
    fontWeight: 500,
    fontSize: TabStyles.Typography.Sizing.Default,
    color: applyColor(DesignTokens.color.font.default.default, CommonStyles.Colors.Grey51),
    letterSpacing: '0.5px',
  });
}
